import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DescriptionBar, Grid, SearchBar } from 'components';
import { promotionGridColumns, activeStatus, formatDate } from './utils';
import PromotionModal from './PromotionModal';
import DeleteModal from './DeleteModal';
import MultipleDeleteModal from './MultipleDeleteModal';
import axiosAuth from 'common/AxiosAuth';

// 이미지 에러 처리를 위한 안전한 컴포넌트
const ImageWithErrorFallback = ({ 
  imageUrl, 
  alt, 
  className 
}: { 
  imageUrl: string; 
  alt: string; 
  className: string; 
}) => {
  const { t } = useTranslation();
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
    console.log('Image loaded successfully:', imageUrl);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
    console.error('Image load failed:', imageUrl);
  };

  if (hasError) {
    return (
      <div className="w-16 h-12 bg-red-50 rounded flex flex-col items-center justify-center p-1">
        <span className="text-xs text-red-500 font-medium">{t('managePromotion.imageFallback.loadFailed')}</span>
        <span className="text-xs text-red-400">{imageUrl.substring(0, 30)}...</span>
        <span className="text-xs text-red-300">{t('managePromotion.imageFallback.checkNetwork')}</span>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="w-16 h-12 bg-gray-100 rounded flex items-center justify-center">
        <span className="text-xs text-gray-400">{t('managePromotion.imageFallback.loading')}</span>
      </div>
    );
  }

  return (
    <img 
      src={imageUrl} 
      alt={alt} 
      className={className}
      onLoad={handleLoad}
      onError={handleError}
    />
  );
};

// 로컬 타입 정의
export interface PromotionItem {
  id: number;
  title: string;
  description: string;
  notice: string;
  isActive: boolean;
  startDate: string;
  endDate: string;
  bannerImageUrl?: string;
  bannerImageFileId?: number;
  buttonText: string;
  buttonLink: string;
  createdAt: string;
  updatedAt: string;
  promotionImage?: File;
}

export interface PromotionRequest {
  title: string;
  description: string;
  notice: string;
  isActive: boolean;
  startDate: string;
  endDate: string;
  bannerImageUrl?: string;
  bannerImageFileId?: number;
  buttonText: string;
  buttonLink: string;
  promotionImage?: File;
}

const ManagePromotion = () => {
  const { t } = useTranslation();
  const [promotionData, setPromotionData] = useState<PromotionItem[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [searchValue, setSearchValue] = useState<string>('');
  const pageSize = 10;

  // 모달 및 다건 삭제 상태
  const [isPromotionModalOpened, setIsPromotionModalOpened] = useState(false);
  const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState<PromotionItem | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [checkedRows, setCheckedRows] = useState<number[]>([]);
  const [isMultipleDeleteModalOpened, setIsMultipleDeleteModalOpened] = useState(false);

  // 이미지 URL 생성 함수
  const getPromotionImageUrl = (imagePath: string | null) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    const baseUrl = process.env.NODE_ENV === 'production' 
      ? 'https://iopsspace.com' 
      : 'http://localhost:8080';
    const fullUrl = `${baseUrl}${imagePath}`;
    console.log('Full Image URL:', fullUrl);
    return fullUrl;
  };

  // 프로모션 데이터 조회
  const fetchPromotions = async (page = 1, search = '') => {
    try {
      setModalLoading(true);
      const searchParams: any = {
        page: page - 1,
        size: pageSize,
        sortBy: 'createdDate',
        sortDirection: 'DESC'
      };
      if (search.trim()) {
        searchParams.keyword = search;
      }
      const response = await axiosAuth.get('/api/admin/promotions/search', { params: searchParams });
      const responseData = response.data.data || response.data;
      const content = responseData.content || [];
      const localPromotions: PromotionItem[] = content.map((apiPromotion: any) => {
        if (!apiPromotion.promotionId) {
          return null;
        }
        return {
          id: apiPromotion.promotionId,
          title: apiPromotion.title || '',
          description: apiPromotion.description || '',
          notice: apiPromotion.notice || '',
          isActive: apiPromotion.active || false,
          startDate: apiPromotion.promotionStartDate || '',
          endDate: apiPromotion.promotionEndDate || '',
          bannerImageUrl: apiPromotion.imagePath || '',
          bannerImageFileId: undefined,
          buttonText: apiPromotion.buttonText || '',
          buttonLink: apiPromotion.buttonLink || '',
          createdAt: apiPromotion.createdDate || '',
          updatedAt: apiPromotion.updatedDate || '',
        };
      }).filter(Boolean) as PromotionItem[];
      setPromotionData(localPromotions);
      setTotalPages(responseData.totalPages || 1);
      setCurrentPage(page);
    } catch (error: any) {
      console.error('프로모션 조회 실패:', error);
      setPromotionData([]);
      if (error.response?.status === 500) {
        console.error('서버 내부 오류가 발생했습니다. 관리자에게 문의하세요.');
      }
    } finally {
      setModalLoading(false);
    }
  };

  useEffect(() => {
    const loadPromotions = async () => {
      try {
        if (searchValue.trim() === '') {
          await fetchPromotions(currentPage);
        } else {
          await fetchPromotions(currentPage, searchValue);
        }
      } catch (error) {
        console.error('페이지 로드 시 프로모션 조회 실패:', error);
      }
    };
    loadPromotions();
  }, [currentPage, searchValue]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchPromotions(1, searchValue);
  };

  const handleReset = () => {
    setSearchValue('');
    setCurrentPage(1);
    fetchPromotions(1, '');
  };

  const handleCreatePromotion = () => {
    setSelectedPromotion(null);
    setIsPromotionModalOpened(true);
  };

  const handleEditPromotion = (promotion: PromotionItem) => {
    setSelectedPromotion(promotion);
    setIsPromotionModalOpened(true);
  };

  const handleDeletePromotion = (promotion: PromotionItem) => {
    setSelectedPromotion(promotion);
    setIsDeleteModalOpened(true);
  };

  const toDateOnly = (v: string) => (v.match(/^(\d{4}-\d{2}-\d{2})/)?.[1] ?? v.slice(0,10));

  const handleSavePromotion = async (data: PromotionRequest) => {
    try {
      setModalLoading(true);
      const formData = new FormData();
      const promotionData = {
        title: data.title,
        description: data.description,
        notice: data.notice,
        buttonText: data.buttonText,
        buttonLink: data.buttonLink,
        promotionStartDate: toDateOnly(data.startDate),
        promotionEndDate: toDateOnly(data.endDate),
        active: data.isActive,
      };
      formData.append('promotionData', new Blob([JSON.stringify(promotionData)], { type: 'application/json' }));
      if (data.promotionImage) {
        formData.append('promotionImage', data.promotionImage);
      }
      if (selectedPromotion) {
        await axiosAuth.patch(`/api/admin/promotions/${selectedPromotion.id}`, formData);
      } else {
        await axiosAuth.post('/api/admin/promotions', formData);
      }
      setIsPromotionModalOpened(false);
      fetchPromotions(currentPage, searchValue);
    } catch (error: any) {
      console.error('프로모션 저장 실패:', error);
      alert(t('managePromotion.alerts.saveFailed'));
    } finally {
      setModalLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedPromotion) return;
    try {
      setModalLoading(true);
      await axiosAuth.post('/api/admin/promotions/delete', [selectedPromotion.id]);
      setIsDeleteModalOpened(false);
      fetchPromotions(currentPage, searchValue);
    } catch (error) {
      console.error('프로모션 삭제 실패:', error);
      alert(t('managePromotion.alerts.deleteFailed'));
    } finally {
      setModalLoading(false);
    }
  };

  const handleToggleStatus = async (promotion: PromotionItem) => {
    try {
      await axiosAuth.patch(`/api/admin/promotions/${promotion.id}/status`, { active: !promotion.isActive });
      fetchPromotions(currentPage, searchValue);
    } catch (error) {
      console.error('상태 변경 실패:', error);
      alert(t('managePromotion.alerts.statusUpdateFailed'));
    }
  };

  const handleMultipleDelete = async () => {
    if (checkedRows.length === 0) return;
    try {
      setModalLoading(true);
      const selectedIds = checkedRows.map(index => promotionData[index].id);
      await axiosAuth.post('/api/admin/promotions/delete', selectedIds);
      setCheckedRows([]);
      setIsMultipleDeleteModalOpened(false);
      fetchPromotions(currentPage, searchValue);
    } catch (error) {
      console.error('다건 삭제 실패:', error);
      alert(t('managePromotion.alerts.deleteFailed'));
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <div className="flex h-full w-full flex-col">
      <DescriptionBar
        className="md:px-6"
        title={t('managePromotion.pageTitle')}
        description={t('managePromotion.description')}
      />
      
      {/* 검색 및 추가 버튼 영역 */}
      <div className="flex justify-between items-end gap-4 p-4 md:p-6">
        <SearchBar
          value={searchValue}
          setValue={setSearchValue}
          placeholder={t('managePromotion.searchPlaceholder')}
          className="w-[300px]"
        />
        <div className="flex gap-2">
          <Button
            className="h-8"
            type="secondary"
            text={t('managePromotion.buttons.reset')}
            onClick={handleReset}
          />
          <Button
            className="h-8"
            type="secondary"
            text={t('managePromotion.buttons.deleteSelected', { count: checkedRows.length })}
            onClick={() => setIsMultipleDeleteModalOpened(true)}
            disabled={checkedRows.length === 0}
          />
          <Button
            className="h-8"
            type="blue"
            text={t('managePromotion.buttons.addPromotion')}
            onClick={handleCreatePromotion}
          />
        </div>
      </div>
      
      {/* 그리드 영역 */}
      <div className="flex-1 px-6">
        <Grid
          gridColumns={promotionGridColumns}
          gridData={promotionData}
          searchValue={searchValue}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          checkedRows={checkedRows}
          setCheckedRows={setCheckedRows}
          options={{
            useFilter: true,
            useSearch: {
              use: true,
              searchKeys: ['title', 'description', 'notice']
            },
            useCheckbox: true,
            usePagination: {
              use: true,
              maxPage: totalPages
            },
            customCell: {
              bannerImage: (_, row) => {
                const promotion = row as PromotionItem;
                const imageUrl = promotion.bannerImageUrl ? getPromotionImageUrl(promotion.bannerImageUrl) : '';
                if (!imageUrl) {
                  return (
                    <div className="w-16 h-12 bg-gray-100 rounded flex items-center justify-center">
                      <span className="text-xs text-gray-400">{t('managePromotion.imageFallback.noImage')}</span>
                    </div>
                  );
                }
                return (
                  <ImageWithErrorFallback
                    imageUrl={imageUrl}
                    alt="Banner"
                    className="w-24 h-16 object-cover rounded overflow-hidden"
                  />
                );
              },
              isActive: (value, row) => {
                const promotion = row as PromotionItem;
                const status = activeStatus[value ? 'true' : 'false'];
                return (
                  <div
                    title={status.title}
                    className="cursor-pointer"
                    onClick={() => handleToggleStatus(promotion)}
                  >
                    <div className={`flex h-6 w-fit items-center rounded-full border px-3 ${status.className}`}>
                      <span className="text-nowrap text-small font-regular">{status.title}</span>
                    </div>
                  </div>
                );
              },
              startDate: (value) => <span>{formatDate(value)}</span>,
              endDate: (value) => <span>{formatDate(value)}</span>,
              createdAt: (value) => <span>{formatDate(value)}</span>,
              description: (value) => (
                <span className="text-ellipsis overflow-hidden max-w-[200px] block" title={value}>
                  {value}
                </span>
              ),
              notice: (value) => (
                <span className="text-ellipsis overflow-hidden max-w-[200px] block" title={value}>
                  {value || '-'}
                </span>
              ),
              actions: (_, row) => {
                const promotion = row as PromotionItem;
                return (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="cursor-pointer flex flex-row gap-1 items-center rounded border px-2 h-6 text-xs font-regular bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100"
                      onClick={() => handleEditPromotion(promotion)}
                    >
                      <span className="select-none text-nowrap">{t('managePromotion.buttons.edit')}</span>
                    </button>
                    <button
                      type="button"
                      className="cursor-pointer flex flex-row gap-1 items-center rounded border px-2 h-6 text-xs font-regular bg-red-50 text-red-600 border-red-200 hover:bg-red-100"
                      onClick={() => handleDeletePromotion(promotion)}
                    >
                      <span className="select-none text-nowrap">{t('managePromotion.buttons.delete')}</span>
                    </button>
                  </div>
                );
              }
            }
          }}
        />
      </div>

      {/* 프로모션 추가/수정 모달 */}
      <PromotionModal
        isOpened={isPromotionModalOpened}
        data={selectedPromotion}
        onClose={() => setIsPromotionModalOpened(false)}
        onSave={handleSavePromotion}
        loading={modalLoading}
      />

      {/* 삭제 확인 모달 */}
      <DeleteModal
        isOpened={isDeleteModalOpened}
        data={selectedPromotion}
        onClose={() => setIsDeleteModalOpened(false)}
        onConfirm={handleConfirmDelete}
        loading={modalLoading}
      />

      {/* 다건 삭제 확인 모달 */}
      <MultipleDeleteModal
        isOpened={isMultipleDeleteModalOpened}
        onClose={() => setIsMultipleDeleteModalOpened(false)}
        onConfirm={handleMultipleDelete}
        loading={modalLoading}
        selectedCount={checkedRows.length}
      />
    </div>
  );
};

export default ManagePromotion;